import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { parseBestCompaniesHubApiPayload } from './parse-best-companies-hub-api-payload';
import type { CompanyRankingSort } from '../search/perform-ssr-company-search';

export interface FetchBestCompaniesHubFilters {
  /** Exact `industryEn` match. */
  industry?: string;
  /** Employer score ≥60 and recent hiring pace ≤1.5/mo — mirrors utils/best-companies/is-network-worthy. */
  networkWorthy?: boolean;
  /** Free-text company-name search — hits the full index rather than just the top-N by score. */
  query?: string;
  /**
   * Force the wider batch even with no other filter set: used by the hub's remote-friendly chip,
   * which filters client-side (Typesense can't express a remote+hybrid percentage sum as a
   * filter_by clause) and so needs a bigger candidate pool than the default top-30-by-hiring
   * batch to show every real match.
   */
  expand?: boolean;
  /** Segmented sort control state; server default is 'hiring' when omitted. */
  sort?: CompanyRankingSort;
  /** hiringTrend === 'growing', server-side (the "Growing" chip). */
  growing?: boolean;
  /** medianSalaryMin ≥ this figure, server-side (the "Pays €5k+" chip). */
  minSalary?: number;
  /** Exact registryCity match, server-side (the "Near Helsinki" chip). */
  city?: string;
  /** Remote or hybrid share ≥ 50%, resolved server-side over the whole hub cohort (the "Remote-friendly" chip). */
  remote?: boolean;
  /** Employers whose top hired roles match this title, server-side over the whole cohort (the "Hiring my role" chip). */
  role?: string;
  /** The page's "Agencies hidden" switch turned OFF: ask the server to include staffing/consulting agencies. */
  includeAgencies?: boolean;
}

const inflightByKey = new Map<string, Promise<AlgoliaCompanyRecord[]>>();

function keyFor(filters?: FetchBestCompaniesHubFilters): string {
  if (!filters) return '';
  return [
    filters.industry ?? '',
    filters.networkWorthy ? '1' : '',
    filters.query ?? '',
    filters.expand ? '1' : '',
    filters.sort ?? '',
    filters.growing ? '1' : '',
    filters.minSalary ?? '',
    filters.city ?? '',
    filters.remote ? '1' : '',
    filters.role ?? '',
    filters.includeAgencies ? '1' : '',
  ].join('|');
}

async function fetchBestCompaniesHubFromApiOnce(
  filters?: FetchBestCompaniesHubFilters,
): Promise<AlgoliaCompanyRecord[]> {
  try {
    const params = new URLSearchParams();
    if (filters?.industry) params.set('industry', filters.industry);
    if (filters?.networkWorthy) params.set('networkWorthy', '1');
    if (filters?.query) params.set('q', filters.query);
    if (filters?.expand) params.set('expand', '1');
    if (filters?.sort) params.set('sort', filters.sort);
    if (filters?.growing) params.set('growing', '1');
    if (typeof filters?.minSalary === 'number') params.set('minSalary', String(filters.minSalary));
    if (filters?.city) params.set('city', filters.city);
    if (filters?.remote) params.set('remote', '1');
    if (filters?.role) params.set('role', filters.role);
    if (filters?.includeAgencies) params.set('agencies', 'all');
    const qs = params.toString();
    const res = await fetch(`/api/best-companies-hub${qs ? `?${qs}` : ''}`, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return [];
    const json: unknown = await res.json();
    return parseBestCompaniesHubApiPayload(json);
  } catch {
    return [];
  }
}

/**
 * Loads hub rankings in the browser — with no `filters`, when Vike did not pass SSR data (e.g.
 * client-side navigation); with `filters`, to fetch past the default top-N batch so the hub's
 * industry/network-worthy chip filters show every match, not just whichever ones happened to
 * land in the top N by score. Concurrent callers for the same filter combo share one request.
 */
export async function fetchBestCompaniesHubFromApi(
  filters?: FetchBestCompaniesHubFilters,
): Promise<AlgoliaCompanyRecord[]> {
  const key = keyFor(filters);
  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = fetchBestCompaniesHubFromApiOnce(filters).finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }
  return inflight;
}

/**
 * Start loading hub data early (e.g. hover on a link to `/best-companies`) — dedupes with the page hook.
 */
export function prefetchBestCompaniesHubFromApi(): void {
  if (typeof window === 'undefined') return;
  void fetchBestCompaniesHubFromApi();
}
